/**
 * EmptyStateView - Component for displaying empty chat state
 *
 * Shows welcome message, example prompt cards (QW10), and optional
 * suggested questions/tasks when documents are selected.
 * Entry animation: fade-in + slide-up (QW11).
 *
 * Extracted from ChatPanel.tsx to reduce complexity.
 */

import React from 'react';
import {
  MessageSquare,
  TrendingUp,
  PieChart,
  FolderSearch,
  Sparkles,
  Upload,
  FileText,
  Search,
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import SuggestedChips from '../SuggestedChips';
import type { MinimalDocument } from '../../types';
import type { SuggestedStudioTask } from './hooks/useSuggestedContent';

// ─── Static example prompts (always shown) ──────────────────────
const EXAMPLE_PROMPTS = [
  { text: '¿Cuales son las acciones con mayor capitalizacion en Chile?', icon: TrendingUp },
  { text: '¿Que acciones tienen mas inversion de las AFPs?', icon: PieChart },
  { text: '¿Que empresas y documentos tengo en mi biblioteca?', icon: FolderSearch },
  { text: '¿Que puedo hacer en MyChat?', icon: Sparkles },
];

// ─── Onboarding steps (shown below main prompt cards) ───────────
const ONBOARDING_STEPS = [
  { icon: Upload, text: 'Sube un PDF desde la barra lateral' },
  { icon: FileText, text: 'Selecciona documentos para dar contexto' },
  { icon: Search, text: 'Pregunta y obtendras respuestas con citas' },
];

export interface EmptyStateViewProps {
  // Suggested content
  selectedMinimalDocuments: MinimalDocument[] | null;
  showSuggestedQuestions: boolean;
  suggestedQuestions: string[];
  isLoadingSuggestions: boolean;
  suggestionsQuality?: any;
  generationMetadata?: any;
  onSuggestedQuestionClick: (question: string) => void;
  showSuggestedTasks: boolean;
  suggestedTasks: SuggestedStudioTask[];
  isLoadingTasks: boolean;
  onSuggestedTaskClick: (task: SuggestedStudioTask) => void;
}

export function EmptyStateView({
  selectedMinimalDocuments,
  showSuggestedQuestions,
  suggestedQuestions,
  isLoadingSuggestions,
  onSuggestedQuestionClick,
  showSuggestedTasks,
  suggestedTasks,
  isLoadingTasks,
  onSuggestedTaskClick,
}: EmptyStateViewProps) {
  const { theme } = useTheme();

  const hasDocs = selectedMinimalDocuments && selectedMinimalDocuments.length > 0;

  return (
    <div className="min-h-[320px] flex flex-col items-center justify-center text-center py-20 animate-empty-state">
      <div className="max-w-md w-full space-y-6">
        <div>
          {/* Brand-style empty state icon */}
          <div className="w-14 h-14 rounded-2xl bg-nb-accent/10 flex items-center justify-center mx-auto mb-4">
            <MessageSquare className="w-7 h-7 text-nb-accent" />
          </div>
          <h1 className={`text-lg font-medium mb-1 ${theme.colors.text.primary}`}>
            Bienvenido a MyChat
          </h1>
          <p className={`text-sm ${theme.colors.text.secondary}`}>
            Analiza documentos financieros del mercado chileno con inteligencia artificial. Los
            documentos se seleccionan automaticamente.
          </p>
        </div>

        {/* QW10: Example prompt cards — always shown when no docs are selected */}
        {!hasDocs && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {EXAMPLE_PROMPTS.map((prompt, index) => (
                <button
                  key={prompt.text}
                  type="button"
                  onClick={() => onSuggestedQuestionClick(prompt.text)}
                  className={`text-left px-3 py-2.5 rounded-lg border ${theme.colors.border.primary} ${theme.colors.bg.secondary} hover:border-nb-accent/50 hover:bg-nb-accent/5 hover:-translate-y-px active:translate-y-0 transition-all group animate-slide-up`}
                  style={{ animationDelay: `${index * 80}ms`, animationFillMode: 'both' }}
                >
                  <div className="flex items-start gap-2">
                    <prompt.icon
                      className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 ${theme.colors.text.muted} group-hover:text-nb-accent transition-colors`}
                    />
                    <span className={`text-xs leading-relaxed ${theme.colors.text.secondary}`}>
                      {prompt.text}
                    </span>
                  </div>
                </button>
              ))}
            </div>

            {/* Onboarding steps — how to get started */}
            <div
              className="animate-slide-up"
              style={{ animationDelay: '350ms', animationFillMode: 'both' }}
            >
              <div className={`flex items-center justify-center gap-6 pt-2`}>
                {ONBOARDING_STEPS.map((step, index) => (
                  <div key={step.text} className="flex items-center gap-1.5">
                    <div
                      className={`w-5 h-5 rounded-full bg-nb-accent/10 flex items-center justify-center flex-shrink-0`}
                    >
                      <span className="text-[10px] font-semibold text-nb-accent">{index + 1}</span>
                    </div>
                    <span className={`text-[11px] ${theme.colors.text.muted}`}>{step.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Suggested chips — unified questions + tasks */}
        {hasDocs && (
          <SuggestedChips
            questions={showSuggestedQuestions ? suggestedQuestions : []}
            tasks={showSuggestedTasks ? suggestedTasks : []}
            onQuestionClick={onSuggestedQuestionClick}
            onTaskClick={onSuggestedTaskClick}
            isLoadingQuestions={showSuggestedQuestions && isLoadingSuggestions}
            isLoadingTasks={showSuggestedTasks && isLoadingTasks}
            className="justify-center"
          />
        )}
      </div>
    </div>
  );
}

export default EmptyStateView;
